import {
	getBroadcastReceiverUserId,
	isOrganizationBroadcast,
	isWorkplaceBroadcast,
} from 'core/utils/broadcastInbox';

export type BroadcastReplyMode = 'author' | 'all';

export type BroadcastReplyLocationState = {
	replyToId: number;
	replyMode: BroadcastReplyMode;
	threadRootId: number;
	/** Null when replying to the broadcast itself. Set when replying to a thread message. */
	parentThreadMessageId: number | null;
	/** Thread message used to prefill a nested reply. */
	replySource?: unknown;
	/** Copied from the original broadcast's isAllowReplyAll flag. */
	allowReplyAll?: boolean;
};

export type ReplyPermissionFlags = {
	canCreate: boolean;
	canCreateOrganization: boolean;
	canCreateWorkplaceWide: boolean;
};

export type ReplyAudience = {
	receiverType: 'User' | 'Broadcast Group';
	id: number;
	label: string;
};

type Person = {
	id?: number;
	firstName?: string;
	lastName?: string;
	'@id'?: string;
};

function asRecord(value: unknown): Record<string, unknown> | null {
	if (!value || typeof value !== 'object') return null;
	return value as Record<string, unknown>;
}

function parseIriId(value: unknown): number | null {
	if (typeof value === 'number' && Number.isFinite(value)) return value;
	if (typeof value === 'string') {
		const parsed = parseInt(value.split('/').pop() || '', 10);
		return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
	}
	const record = asRecord(value);
	if (!record) return null;
	if (record.id != null) {
		const parsed = Number(record.id);
		return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
	}
	if (typeof record['@id'] === 'string') return parseIriId(record['@id']);
	return null;
}

export function getPersonName(person: unknown): string {
	const record = asRecord(person);
	if (!record) return 'N/A';
	const name = `${record.firstName || ''} ${record.lastName || ''}`.trim();
	return name || 'N/A';
}

export function getThreadRootId(message: unknown): number {
	const record = asRecord(message);
	const ownId = parseIriId(record?.id) || 0;
	if (!record) return ownId;
	const fromRoot = parseIriId(record.threadRoot);
	return fromRoot || ownId;
}

export function formatReplyTitle(title: unknown): string {
	const trimmed = String(title || '').trim();
	if (/^re:\s/i.test(trimmed)) return trimmed;
	return `Re: ${trimmed || 'Broadcast'}`;
}

export function buildReplyQuote(content: unknown): string {
	const text = String(content || '')
		.replace(/<[^>]+>/g, ' ')
		.replace(/&nbsp;/g, ' ')
		.replace(/\s+/g, ' ')
		.trim()
		.slice(0, 280);
	if (!text) return '';
	const safe = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
	return `<blockquote>${safe}</blockquote><p><br></p>`;
}

export function canReplyToAuthor(flags: ReplyPermissionFlags): boolean {
	return flags.canCreate;
}

/** True only when the original broadcast explicitly allows reply-all. */
export function isReplyAllAllowed(message: unknown): boolean {
	return asRecord(message)?.isAllowReplyAll === true;
}

export function canReplyToAll(
	message: unknown,
	flags: ReplyPermissionFlags,
	allowReplyAll: boolean = isReplyAllAllowed(message),
): boolean {
	if (!flags.canCreate || allowReplyAll !== true) return false;
	if (isOrganizationBroadcast(message)) return flags.canCreateOrganization;
	if (isWorkplaceBroadcast(message)) return flags.canCreateWorkplaceWide;
	return true;
}

export function resolveReplyTarget(
	message: unknown,
	mode: BroadcastReplyMode,
	currentUserId: number | null,
): ReplyAudience | null {
	const record = asRecord(message);
	if (!record) return null;

	const sender = record.sender as Person | undefined;
	const senderId = parseIriId(sender);

	if (mode === 'author') {
		if (!senderId) return null;
		return { receiverType: 'User', id: senderId, label: getPersonName(sender) };
	}

	const group = asRecord(record.receiverGroup);
	const groupId = parseIriId(record.receiverGroup);
	if (groupId && (group || typeof record.receiverGroup === 'string')) {
		const label = group ? String(group.groupName || `Group ${groupId}`) : `Group ${groupId}`;
		return { receiverType: 'Broadcast Group', id: groupId, label };
	}

	const receiverId = getBroadcastReceiverUserId(message);
	const targetId =
		currentUserId != null && receiverId === currentUserId ? senderId : receiverId || senderId;
	if (!targetId) return null;

	const labelSource = targetId === senderId ? sender : record.receiverUser;
	return { receiverType: 'User', id: targetId, label: getPersonName(labelSource) };
}

export function withParentMessage<T extends Record<string, unknown>>(
	body: T,
	replyToId: number,
): T & { parentMessage: string } {
	return {
		...body,
		parentMessage: `/api/broadcast_messages/${replyToId}`,
	};
}

export type ThreadReplyPayload = {
	parentMessage: string | null;
	receiverUser: string | null;
	receiverGroup: string | null;
	title: string;
	content: string;
	priority: string;
	replyType: BroadcastReplyMode;
};

export function buildThreadReplyPayload(args: {
	parentThreadMessageId: number | null;
	replyType: BroadcastReplyMode;
	receiverType: 'User' | 'Broadcast Group';
	selectedValue: number | string;
	title: string;
	content: string;
	priorityId: number | string;
}): ThreadReplyPayload {
	const isUser = args.receiverType === 'User';
	return {
		parentMessage: args.parentThreadMessageId
			? `/api/broadcast_thread_messages/${args.parentThreadMessageId}`
			: null,
		receiverUser: isUser ? `/api/users/${args.selectedValue}` : null,
		receiverGroup: isUser ? null : `/api/broadcast_groups/${args.selectedValue}`,
		title: args.title,
		content: args.content,
		priority: `/api/priorities/${args.priorityId}`,
		replyType: args.replyType,
	};
}

/** Id of the broadcast this thread entry belongs to. */
export function getBroadcastMessageId(message: unknown): number {
	const record = asRecord(message);
	if (!record) return 0;
	return parseIriId(record.broadcastMessage) || 0;
}

export type BroadcastListItem = {
	id: number;
	isThreadMessage?: boolean;
	broadcastMessageId?: number | null;
	threadRootId?: number;
	[key: string]: unknown;
};

export function mapThreadEntry<T>(
	entry: T,
): T & { id: number; broadcastMessageId: number | null; threadRootId: number; isThreadMessage: true } {
	const record = asRecord(entry) || {};
	const threadId = parseIriId(record.id) || 0;
	const broadcastMessageId = parseIriId(record.broadcastMessage);
	const threadRootId = parseIriId(record.threadRoot) || broadcastMessageId || threadId;
	return {
		...(record as T),
		id: threadId,
		broadcastMessageId,
		threadRootId,
		isThreadMessage: true,
	};
}

export function normalizeThreadResponse(data: unknown): unknown[] {
	return normalizeBroadcastListResponse(data);
}

export function normalizeBroadcastListResponse(data: unknown): unknown[] {
	if (Array.isArray(data)) return data;
	const record = asRecord(data);
	if (!record) return [];
	if (Array.isArray(record.member)) return record.member;
	if (Array.isArray(record.items)) return record.items;
	if (Array.isArray(record['hydra:member'])) return record['hydra:member'] as unknown[];
	return [];
}

export function mapThreadListEntries(data: unknown): BroadcastListItem[] {
	return normalizeBroadcastListResponse(data).map((entry) => mapThreadEntry(entry));
}

export function mergeBroadcastAndThreadLists(
	broadcasts: unknown[],
	threads: unknown[],
): BroadcastListItem[] {
	const mappedBroadcasts: BroadcastListItem[] = broadcasts.map((item) => {
		const record = asRecord(item) || {};
		const id = parseIriId(record.id) || 0;
		return {
			...(record as BroadcastListItem),
			id,
			isThreadMessage: false,
		};
	});
	const mappedThreads = threads.map((entry) =>
		asRecord(entry)?.isThreadMessage === true ? (entry as BroadcastListItem) : mapThreadEntry(entry),
	);
	const seen = new Set<string>();
	return [...mappedBroadcasts, ...mappedThreads].filter((item) => {
		const key = `${item.isThreadMessage ? 'thread' : 'broadcast'}-${item.id}`;
		if (seen.has(key)) return false;
		seen.add(key);
		return true;
	});
}

export function getListOpenBroadcastId(message: unknown): number {
	const record = asRecord(message);
	if (!record) return 0;
	if (record.isThreadMessage) {
		return (
			parseIriId(record.threadRootId) ||
			parseIriId(record.threadRoot) ||
			parseIriId(record.broadcastMessageId) ||
			parseIriId(record.broadcastMessage) ||
			0
		);
	}
	return parseIriId(record.id) || 0;
}

export function listItemKey(message: unknown): string {
	const record = asRecord(message);
	const id = parseIriId(record?.id) || 0;
	return record?.isThreadMessage ? `thread-${id}` : `broadcast-${id}`;
}

function messageTime(message: unknown): number {
	const record = asRecord(message);
	if (!record) return 0;
	const iso = record.createdAt || record.created_at;
	if (typeof iso === 'string') {
		const parsed = Date.parse(iso);
		if (!Number.isNaN(parsed)) return parsed;
	}
	if (typeof record.createdDate === 'string') {
		const combined = `${record.createdDate} ${record.createdTime || '00:00:00'}`;
		const parsed = Date.parse(combined);
		if (!Number.isNaN(parsed)) return parsed;
	}
	return parseIriId(record.id) || 0;
}

export function sortThreadChronologically<T>(messages: T[]): T[] {
	return [...messages].sort((a, b) => messageTime(a) - messageTime(b));
}

/** Thread GET returns replies only. Keep the original broadcast at the top of the conversation. */
export function prependOriginalBroadcast<T, R>(original: T | null | undefined, replies: R[]): Array<T | R> {
	if (!original) return replies;
	const originalId = parseIriId(asRecord(original)?.id);
	const rest = replies.filter((reply) => {
		const record = asRecord(reply);
		if (!record) return true;
		if (record.broadcastMessage || record.broadcastMessageId) return true;
		return parseIriId(record.id) !== originalId;
	});
	return [original, ...rest];
}

export function getParentTitle(message: unknown): string | null {
	const record = asRecord(message);
	const parent = asRecord(record?.parentMessage);
	if (!parent) return null;
	const title = parent.title;
	return typeof title === 'string' && title.trim() ? title : null;
}

export function getParentMessageId(message: unknown): number | null {
	const record = asRecord(message);
	if (!record || record.parentMessage == null) return null;
	return parseIriId(record.parentMessage);
}

export function resolveParentTitle(message: unknown, thread: unknown[]): string | null {
	const embedded = getParentTitle(message);
	if (embedded) return embedded;
	const parentId = getParentMessageId(message);
	if (!parentId) return null;
	const parent = thread.find((item) => parseIriId(asRecord(item)?.id) === parentId);
	const title = asRecord(parent)?.title;
	return typeof title === 'string' && title.trim() ? title : null;
}

export function getReceiverLabel(message: unknown): string {
	const record = asRecord(message);
	if (!record) return 'N/A';
	if (record.receiverUser) return getPersonName(record.receiverUser);
	const group = asRecord(record.receiverGroup);
	if (group) return String(group.groupName || `Group ${group.id || ''}`) || 'N/A';
	if (typeof record.receiverGroup === 'string') return 'Broadcast Group';
	return 'N/A';
}
